import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { TagList } from '../components/TagBadge';
import { fmtDate } from '../utils/dates';
import { fmtMoney } from '../utils/money';
import ServicesEditor from '../components/ServicesEditor';
import { printInvoice } from '../utils/printInvoice';

const STATUS_AR = {
  pending: 'معلّقة', in_progress: 'قيد التنفيذ', waiting_approval: 'بانتظار الاعتماد',
  approved: 'معتمدة', revision: 'مراجعة', completed: 'مكتملة', cancelled: 'ملغاة',
};

const INV_STATUS_AR = { draft: 'مسودة', issued: 'صادرة', paid: 'مدفوعة', cancelled: 'ملغاة' };
const INV_STATUS_BADGE = { draft: 'pending', issued: 'in_progress', paid: 'completed', cancelled: 'cancelled' };
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function ProjectDetail() {
  const { id } = useParams();
  const { api, user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [tagFilter, setTagFilter] = useState('');

  const [invoices, setInvoices] = useState([]);
  const [editingServices, setEditingServices] = useState(false);
  const [servicesDraft, setServicesDraft] = useState([]);
  const [savingServices, setSavingServices] = useState(false);
  const [showGenModal, setShowGenModal] = useState(false);
  const [genForm, setGenForm] = useState({ issue_date: todayStr(), due_date: '', status: 'issued', tax_rate: '', discount: '' });
  const [generating, setGenerating] = useState(false);

  const canManageInvoices = ['admin', 'supervisor', 'team'].includes(user?.role);

  const load = () => {
    setLoading(true);
    api(`/projects/${id}`)
      .then(setProject)
      .catch(err => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const loadInvoices = () => {
    if (!canManageInvoices) return;
    api(`/projects/${id}/invoices`).then(setInvoices).catch(() => {});
  };

  useEffect(load, [id]);
  useEffect(loadInvoices, [id]);

  const tasks = project?.tasks || [];

  const allTags = useMemo(() => {
    const map = new Map();
    tasks.forEach(t => (t.tags || []).forEach(tag => map.set(tag.id, tag)));
    return Array.from(map.values());
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    let out = tasks;
    if (tab !== 'all') {
      const sheikhId = parseInt(tab, 10);
      out = out.filter(t => t.sheikh_id === sheikhId);
    }
    if (tagFilter) {
      const tid = parseInt(tagFilter, 10);
      out = out.filter(t => (t.tags || []).some(tg => tg.id === tid));
    }
    return out;
  }, [tasks, tab, tagFilter]);

  const tasksBySheikh = useMemo(() => {
    const groups = {};
    tasks.forEach(t => {
      const k = t.sheikh_id || 0;
      if (!groups[k]) groups[k] = [];
      groups[k].push(t);
    });
    return groups;
  }, [tasks]);

  const canEdit = ['admin', 'supervisor', 'team'].includes(user?.role);

  const services = project?.services || [];
  const servicesTotal = useMemo(
    () => services.reduce((sum, s) => sum + (Number(s.quantity) || 0) * (Number(s.unit_price) || 0), 0),
    [services]
  );

  const startEditServices = () => {
    setServicesDraft(services.map(s => ({
      name: s.name, unit: s.unit || '', quantity: s.quantity, unit_price: s.unit_price,
    })));
    setEditingServices(true);
  };

  const saveServices = async () => {
    setSavingServices(true);
    try {
      const clean = servicesDraft
        .filter(s => s.name && s.name.trim())
        .map(s => ({
          name: s.name.trim(),
          unit: s.unit?.trim() || null,
          quantity: Number(s.quantity) || 0,
          unit_price: Number(s.unit_price) || 0,
        }));
      await api(`/projects/${id}`, { method: 'PUT', body: JSON.stringify({ services: clean }) });
      toast.success('تم حفظ الخدمات');
      setEditingServices(false);
      load();
    } catch (err) { toast.error(err.message); }
    finally { setSavingServices(false); }
  };

  const generateInvoice = async () => {
    setGenerating(true);
    try {
      const payload = {
        issue_date: genForm.issue_date || undefined,
        due_date: genForm.due_date || undefined,
        status: genForm.status,
        tax_rate: Number(genForm.tax_rate) || 0,
        discount: Number(genForm.discount) || 0,
      };
      await api(`/projects/${id}/invoices`, { method: 'POST', body: JSON.stringify(payload) });
      toast.success('تم إصدار الفاتورة');
      setShowGenModal(false);
      setGenForm({ issue_date: todayStr(), due_date: '', status: 'issued', tax_rate: '', discount: '' });
      loadInvoices();
    } catch (err) { toast.error(err.message); }
    finally { setGenerating(false); }
  };

  const printInvoiceById = async (inv) => {
    try {
      const full = await api(`/invoices/${inv.id}`);
      printInvoice(full, project.title);
    } catch (err) { toast.error(err.message); }
  };

  // الفاتورة لا تُحذف — الرقم يبقى محجوزاً والسجلّ قائماً
  const cancelInvoice = async (inv) => {
    if (!window.confirm(`إلغاء الفاتورة ${inv.invoice_number}؟ الرقم يبقى محجوزاً ولا يُعاد استخدامه.`)) return;
    try {
      await api(`/invoices/${inv.id}/cancel`, { method: 'POST' });
      toast.success('تم إلغاء الفاتورة');
      loadInvoices();
    } catch (err) { toast.error(err.message); }
  };

  if (loading) return <div className="loader">جاري التحميل...</div>;
  if (!project) return <div className="empty">المشروع غير موجود</div>;

  const sheikhs = project.sheikhs || [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Link to="/projects" style={{ fontSize: 13, color: 'var(--text-muted)' }}>← المشاريع</Link>
          <h2 style={{ margin: '4px 0 0' }}>{project.title}</h2>
        </div>
        {canEdit && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => navigate(`/tasks?project_id=${project.id}&new=1`)}
          >+ مهمة في هذا المشروع</button>
        )}
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, fontSize: 13 }}>
          <div>
            <div style={{ color: 'var(--text-muted)', marginBottom: 4 }}>الحالة</div>
            <span className={`badge badge-${project.status === 'active' ? 'in_progress' : project.status === 'completed' ? 'completed' : 'pending'}`}>
              {project.status}
            </span>
          </div>
          {project.sponsor && (
            <div>
              <div style={{ color: 'var(--text-muted)', marginBottom: 4 }}>الداعم / صاحب المشروع</div>
              <Link to={`/reports/sponsor/${project.sponsor.id}`}>{project.sponsor.name}</Link>
              {project.sponsor.type && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}> ({project.sponsor.type === 'institution' ? 'مؤسسة' : 'شخص'})</span>}
            </div>
          )}
          {project.client && (
            <div>
              <div style={{ color: 'var(--text-muted)', marginBottom: 4 }}>العميل</div>
              {project.client.name}
            </div>
          )}
          <div>
            <div style={{ color: 'var(--text-muted)', marginBottom: 4 }}>إجمالي المهام</div>
            <strong>{tasks.length}</strong>
          </div>
          {project.members && project.members.length > 0 && (
            <div>
              <div style={{ color: 'var(--text-muted)', marginBottom: 4 }}>فريق العمل ({project.members.length})</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {project.members.map(m => (
                  <span key={m.id} className="badge badge-info">{m.name}</span>
                ))}
              </div>
            </div>
          )}
        </div>
        {project.description && (
          <div style={{ fontSize: 13, marginTop: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            {project.description}
          </div>
        )}
      </div>

      {canEdit && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div className="card-title">الخدمات والتسعير</div>
            {!editingServices && (
              <button className="btn btn-sm btn-outline" onClick={startEditServices}>
                {services.length ? 'تعديل الخدمات' : '+ إضافة خدمات'}
              </button>
            )}
          </div>

          {editingServices ? (
            <div>
              <ServicesEditor value={servicesDraft} onChange={setServicesDraft} />
              <div className="modal-actions" style={{ marginTop: 12 }}>
                <button className="btn btn-primary btn-sm" onClick={saveServices} disabled={savingServices}>
                  {savingServices ? 'جارٍ الحفظ…' : 'حفظ'}
                </button>
                <button className="btn btn-outline btn-sm" onClick={() => setEditingServices(false)} disabled={savingServices}>إلغاء</button>
              </div>
            </div>
          ) : services.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              لم تُحدَّد خدمات لهذا المشروع بعد. أضِف الخدمات وأسعارها لتصدر الفاتورة بناءً عليها.
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>الخدمة</th><th>الوحدة</th><th>الكمية</th><th>سعر الوحدة</th><th>الإجمالي</th></tr>
                </thead>
                <tbody>
                  {services.map(s => (
                    <tr key={s.id}>
                      <td style={{ fontWeight: 500 }}>{s.name}</td>
                      <td>{s.unit || '—'}</td>
                      <td>{fmtMoney(s.quantity)}</td>
                      <td>{fmtMoney(s.unit_price)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtMoney((Number(s.quantity) || 0) * (Number(s.unit_price) || 0))}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'left', fontWeight: 600 }}>الإجمالي المقدّر</td>
                    <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtMoney(servicesTotal)} ريال</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {canManageInvoices && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div className="card-title">الفواتير ({invoices.length})</div>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => setShowGenModal(true)}
              disabled={services.length === 0}
              title={services.length === 0 ? 'أضِف خدمات للمشروع أولاً' : ''}
            >+ إصدار فاتورة</button>
          </div>
          {invoices.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>لا توجد فواتير بعد.</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>رقم الفاتورة</th><th>التاريخ</th><th>الحالة</th><th>الإجمالي</th><th></th></tr>
                </thead>
                <tbody>
                  {invoices.map(inv => (
                    <tr key={inv.id}>
                      <td style={{ fontWeight: 500 }}>{inv.invoice_number}</td>
                      <td>{fmtDate(inv.issue_date)}</td>
                      <td><span className={`badge badge-${INV_STATUS_BADGE[inv.status]}`}>{INV_STATUS_AR[inv.status]}</span></td>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtMoney(inv.total)} ريال</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className="btn btn-sm btn-outline" onClick={() => printInvoiceById(inv)} title="طباعة / PDF">🖨</button>
                        {!inv.is_frozen && (
                          <button className="btn btn-sm btn-outline" style={{ color: 'var(--danger)', marginInlineStart: 6 }} onClick={() => cancelInvoice(inv)} title="إلغاء">🚫</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div className="card-title" style={{ marginBottom: 12 }}>
          المشايخ المستفيدون ({sheikhs.length})
        </div>
        {sheikhs.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            لم يُضَف مشايخ لهذا المشروع. عدّل المشروع لإضافتهم.
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {sheikhs.map(s => {
              const cnt = (tasksBySheikh[s.id] || []).length;
              return (
                <button
                  key={s.id}
                  className={`btn btn-sm ${tab === String(s.id) ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => setTab(String(s.id))}
                >
                  👳 {s.name} <span className="badge badge-info" style={{ marginInlineStart: 6 }}>{cnt}</span>
                  <Link
                    to={`/reports/sheikh/${s.id}?project_id=${project.id}`}
                    onClick={e => e.stopPropagation()}
                    style={{ marginInlineStart: 8, fontSize: 11 }}
                    title="تقرير الشيخ"
                  >📊</Link>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="filters" style={{ marginBottom: 16 }}>
        <button
          className={`btn btn-sm ${tab === 'all' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setTab('all')}
        >جميع المهام ({tasks.length})</button>
        {sheikhs.map(s => (
          <button
            key={s.id}
            className={`btn btn-sm ${tab === String(s.id) ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setTab(String(s.id))}
          >{s.name} ({(tasksBySheikh[s.id] || []).length})</button>
        ))}
        {tasksBySheikh[0]?.length > 0 && (
          <button
            className={`btn btn-sm ${tab === '0' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setTab('0')}
          >بدون شيخ ({tasksBySheikh[0].length})</button>
        )}
        {allTags.length > 0 && (
          <select className="form-select" style={{ marginInlineStart: 'auto' }}
            value={tagFilter} onChange={e => setTagFilter(e.target.value)}>
            <option value="">كل الوسوم</option>
            {allTags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
      </div>

      <div className="card">
        <div className="card-title" style={{ marginBottom: 12 }}>
          المهام ({filteredTasks.length})
        </div>
        {filteredTasks.length === 0 ? (
          <div className="empty">لا توجد مهام مطابقة</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>المهمة</th>
                  <th>الشيخ</th>
                  <th>المنفّذ</th>
                  <th>الحالة</th>
                  <th>الوسوم</th>
                  <th>الاستحقاق</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map(t => (
                  <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/tasks/${t.id}`)}>
                    <td style={{ fontWeight: 500 }}>{t.title}</td>
                    <td>{t.sheikh?.name || '—'}</td>
                    <td>{t.assignee?.name || '—'}</td>
                    <td><span className={`badge badge-${t.status}`}>{STATUS_AR[t.status] || t.status}</span></td>
                    <td><TagList tags={t.tags || []} /></td>
                    <td>{t.due_date ? fmtDate(t.due_date) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showGenModal && (
        <div className="modal-overlay" onClick={() => setShowGenModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="modal-title">إصدار فاتورة</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
              تُصدَر الفاتورة بلقطة من خدمات المشروع الحالية ({services.length} خدمة، إجمالي {fmtMoney(servicesTotal)} ريال).
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">تاريخ الإصدار</label>
                <input type="date" className="form-input" value={genForm.issue_date}
                  onChange={e => setGenForm(f => ({ ...f, issue_date: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">تاريخ الاستحقاق (اختياري)</label>
                <input type="date" className="form-input" value={genForm.due_date}
                  onChange={e => setGenForm(f => ({ ...f, due_date: e.target.value }))} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">نسبة الضريبة (%)</label>
                <input type="number" min="0" max="100" step="0.01" className="form-input" value={genForm.tax_rate}
                  placeholder="0"
                  onChange={e => setGenForm(f => ({ ...f, tax_rate: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">الخصم (ريال)</label>
                <input type="number" min="0" step="0.01" className="form-input" value={genForm.discount}
                  placeholder="0"
                  onChange={e => setGenForm(f => ({ ...f, discount: e.target.value }))} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">الحالة</label>
              <select className="form-select" value={genForm.status}
                onChange={e => setGenForm(f => ({ ...f, status: e.target.value }))}>
                {Object.entries(INV_STATUS_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={generateInvoice} disabled={generating}>
                {generating ? 'جارٍ الإصدار…' : 'إصدار'}
              </button>
              <button className="btn btn-outline" onClick={() => setShowGenModal(false)} disabled={generating}>إلغاء</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
